import { Box, Portal, SxProps, Theme } from '@mui/material';
import { ReactNode, createContext, useContext } from 'react';

/* The id of a DOM node, outside the dialog, that popouts inside the dialog can render into.
 *
 * The shell's paper clips its overflow so the body can scroll, which cuts off any popout that
 * extends past a paper edge — a date picker's calendar being the case that matters. Fields
 * that render their own popout read this id and portal into it instead.
 *
 * Null outside a shell, so a field outside a dialog keeps rendering its popout inline.
 */
const DialogShellPortalContext = createContext<string | null>(null);

export const useDialogShellPortalId = (): string | null => useContext(DialogShellPortalContext);

/* Covers the viewport above the dialog, passing pointer events through everywhere except the
 * popouts themselves.
 *
 * The z-index sits on the host rather than on the popout: the app forces
 * `.react-datepicker-popper { z-index: 4 !important }` globally, and a host with its own
 * stacking context lifts the whole popout above the dialog without touching that rule.
 */
const hostSx: SxProps<Theme> = {
    position: 'fixed',
    inset: 0,
    pointerEvents: 'none',
    zIndex: (theme) => theme.zIndex.modal + 1,
    '& > *': {
        pointerEvents: 'auto',
    },
};

type DialogShellPortalHostProps = {
    id: string;
    // Only a dialog that is open can have a popout to host.
    isActive: boolean;
    children: ReactNode;
};

// The host itself goes to the document body: rendered within the dialog it would be clipped
// by the same paper the popouts are escaping.
export const DialogShellPortalHost = ({ id, isActive, children }: DialogShellPortalHostProps) => (
    <DialogShellPortalContext.Provider value={id}>
        {isActive ? (
            <Portal>
                <Box id={id} className={'axle-dialog-shell-portal-host'} sx={hostSx} />
            </Portal>
        ) : null}
        {children}
    </DialogShellPortalContext.Provider>
);
